import { useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import usePagination from '@hooks/usePagination';
import { policyTableHeader } from '@lib/data';
import { SEE_POLICY_LIST } from '@graphql/queries';
import { Query, Policy as PolicyType } from '@graphql/types';
import { getPolicyType } from '@lib/enum';

import { FilterBox, Contents } from '@components/styled/StyledComponents';
import Headline from '@components/layout/Headline';
import StyledSelect from '@components/styled/StyledSelect';
import TableHeader from '@components/share/Table/TableHeader';
import { TableBody, EmptyTable } from '@components/share/Table/table.style';
import PolicyTableRow from '@components/feature/Operation/Policy/PolicyTableRow';
import Pagination from '@components/layout/Pagination';
import Loader from '@components/share/Loader';

const Policy = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    // 페이지네이션 관련 상태
    const [take, setTake] = useState(10);
    const [preCursor, setPreCursor] = useState(0);
    const [nextCursor, setNextCursor] = useState(0);
    const [cursorOption, setCursorOption] = useState<boolean | null>(null);
    const [filter, setFilter] = useState({ type: '전체', order: 1 });

    const { data, loading } = useQuery<Pick<Query, 'seePolicyList'>>(
        SEE_POLICY_LIST,
        {
            variables: {
                type: getPolicyType(filter.type as string),
                orderBy: +filter.order,
                take,
                preCursor,
                nextCursor,
                cursorOption,
                searchTerm
            },
            onError: (err) => {
                console.error(err);
            }
        }
    );

    const policyList = data?.seePolicyList?.policyList ?? [];
    const totalLength = data?.seePolicyList?.totalLength ?? 0;

    const onFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilter((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    // 페이지네이션
    const {
        pageIndex,
        setPageIndex,
        pageGroupLength,
        prevPageHandler,
        nextPageHandler,
        prevPageGroupHandler,
        nextPageGroupHandler,
        resetPaginationHandler,
        disabled
    } = usePagination(
        take,
        totalLength,
        setPreCursor,
        setNextCursor,
        setCursorOption,
        policyList,
        'pcyId'
    );

    return (
        <Contents>
            <Headline
                title="방침 및 규약"
                subTitle="펫이닷 가입자 개인정보 처리방침 및 이용약관"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchCount={totalLength}
                buttonList={['생성']}
                buttonAction={[() => navigate('/operation/policy/new')]}
                SEARCH
            />
            {/*  필터 영역 */}
            <FilterBox>
                <StyledSelect
                    title="구분"
                    name="type"
                    value={filter.type}
                    onChange={onFilterChange}
                    options={[
                        '전체',
                        '이용약관',
                        '개인정보 수집 및 이용 동의',
                        '민감정보 수집 및 이용 동의',
                        '개인정보 제3자 제공 동의',
                        '마케팅 활용 동의',
                        '광고성 정보 수신 동의'
                    ]}
                    width={filter.type.length * 12}
                />
                <StyledSelect
                    title="정렬"
                    name="order"
                    value={filter.order}
                    onChange={onFilterChange}
                    options={[
                        { title: '내림차순', value: 1 },
                        { title: '오름차순', value: 2 }
                    ]}
                    DATA_VALUE
                />
            </FilterBox>
            <TableHeader header={policyTableHeader} />
            <TableBody $minHeight={500}>
                {loading && <Loader />}
                {totalLength === 0 ? (
                    <EmptyTable>방침 및 규약이 없습니다.</EmptyTable>
                ) : (
                    policyList
                        ?.slice(pageIndex, pageIndex + take)
                        .map((data, idx) => (
                            <PolicyTableRow
                                key={`${data?.pcyId}-${idx}-policy-list`}
                                header={policyTableHeader}
                                data={data as PolicyType}
                            />
                        ))
                )}
            </TableBody>
            {totalLength > 0 && (
                <Pagination
                    take={take}
                    setTake={setTake}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    pageGroupLength={pageGroupLength}
                    totalLength={totalLength}
                    prevPageHandler={prevPageHandler}
                    nextPageHandler={nextPageHandler}
                    prevPageGroupHandler={prevPageGroupHandler}
                    nextPageGroupHandler={nextPageGroupHandler}
                    resetPaginationHandler={resetPaginationHandler}
                    disabled={disabled}
                />
            )}
        </Contents>
    );
};

export default Policy;
